import React from 'react';
import { Menu, Dropdown } from 'antd';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { slideAction } from '../../store/slideSlice';
import { slideAPI } from '../../store/api';
import { emitElementAdd } from '../../Socket/Socket';
import { getUpdatedSlides } from '../../utils/helper';
import { WORD_ART_PRESETS, createWordArtTextObject } from './wordArtPresets';
import { buildWordArtDisplayStyles, applyWordArtStyles, requestWordArtEdit, WORD_ART_REF_WIDTH, WORD_ART_REF_HEIGHT } from './wordArtUtils';
import LayoutEnum from '../../utils/layoutEnum';
import classes from './WordArtDropdown.module.css';

const PAGE_WIDTH =
  window.screen.width > 2200 ? 1800 :
  window.screen.width > 1540 ? 1200 :
  window.screen.width > 1200 ? 960 :
  window.screen.width > 992 ? 848 :
  window.screen.width > 768 ? 700 :
  window.screen.width > 490 ? 490 : 300;

const PAGE_HEIGHT =
  window.screen.width > 2200 ? 1020 :
  window.screen.width > 1540 ? 700 :
  window.screen.width > 1200 ? 540 :
  window.screen.width > 992 ? 500 :
  window.screen.width > 768 ? 400 :
  window.screen.width > 490 ? 290 : 180;

const PresetPreview = ({ preset }) => {
  const { textStyles, wrapperStyle } = buildWordArtDisplayStyles(
    preset.effects,
    200,
    40,
    preset.previewText
  );

  return (
    <div className={classes.menuPreviewFrame}>
      <div className={classes.menuPreviewInner} style={wrapperStyle}>
        <span
          className={classes.menuPreviewText}
          style={{
            ...textStyles,
            fontSize: '22px',
            lineHeight: 1,
            display: 'inline-block',
            width: 'auto',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          {preset.previewText}
        </span>
      </div>
    </div>
  );
};

export const WordArtDropdown = ({ currentSlideId, presentationId, onMenuOpenChange }) => {
  const dispatch = useDispatch();
  const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
  const currentSlideData = useSelector(
    (state) => currentSlideId && state.slide.slides[currentSlideId]
  );

  const insertWordArt = (presetId) => {
    if (!currentSlideId) return;

    const preset = WORD_ART_PRESETS[presetId];
    if (!preset) return;

    let slideContent = document.getElementById('slide01');
    if (isMasterSlide) slideContent = document.getElementById('master-slide');
    if (!slideContent) return;

    let activeSlideId = currentSlideId;
    const lockScreen = document.createElement('div');
    lockScreen.setAttribute(
      'style',
      'width:100%;height:100%;position:absolute;background:transparent;cursor:crosshair;z-index:9999999999'
    );
    slideContent.appendChild(lockScreen);

    let draw = false;
    let tempBox;
    let previewWrap;
    let previewTextEl;
    let previewRaf = null;
    let pendingPreview = null;
    const PREVIEW_REF_WIDTH = WORD_ART_REF_WIDTH;
    const PREVIEW_REF_HEIGHT = WORD_ART_REF_HEIGHT;
    let x1;
    let y1;
    let x2;
    let y2;

    const createPreviewContent = () => {
      previewWrap = document.createElement('div');
      previewWrap.style.pointerEvents = 'none';
      previewWrap.style.position = 'absolute';
      previewWrap.style.inset = '0';
      previewWrap.style.display = 'flex';
      previewWrap.style.alignItems = 'center';
      previewWrap.style.justifyContent = 'center';

      previewTextEl = document.createElement('div');
      previewTextEl.style.pointerEvents = 'none';
      previewTextEl.style.textAlign = 'center';
      previewTextEl.style.whiteSpace = 'nowrap';
      previewTextEl.textContent = preset.defaultText;

      const { textStyles } = buildWordArtDisplayStyles(
        preset.effects,
        PREVIEW_REF_WIDTH,
        PREVIEW_REF_HEIGHT,
        preset.defaultText
      );
      applyWordArtStyles(previewTextEl, textStyles);
      previewWrap.appendChild(previewTextEl);
      tempBox.appendChild(previewWrap);
    };

    const updateDrawPreview = (width, height, left, top) => {
      if (!tempBox) return;
      const safeWidth = Math.max(width, 1);
      const safeHeight = Math.max(height, 1);
      const scaleX = safeWidth / PREVIEW_REF_WIDTH;
      const scaleY = safeHeight / PREVIEW_REF_HEIGHT;
      tempBox.style.transform = `translate3d(${left}px, ${top}px, 0) scale(${scaleX}, ${scaleY})`;
    };

    const scheduleDrawPreview = (width, height, left, top) => {
      pendingPreview = { width, height, left, top };
      if (previewRaf !== null) return;
      const tick = () => {
        previewRaf = null;
        const next = pendingPreview;
        pendingPreview = null;
        if (!next) return;
        updateDrawPreview(next.width, next.height, next.left, next.top);
        if (pendingPreview) {
          previewRaf = requestAnimationFrame(tick);
        }
      };
      previewRaf = requestAnimationFrame(tick);
    };

    const mouseDown = (e) => {
      e.preventDefault();
      draw = true;
      x1 = e.offsetX;
      y1 = e.offsetY;
      tempBox = document.createElement('div');
      tempBox.style.position = 'absolute';
      tempBox.style.left = '0';
      tempBox.style.top = '0';
      tempBox.style.width = `${PREVIEW_REF_WIDTH}px`;
      tempBox.style.height = `${PREVIEW_REF_HEIGHT}px`;
      tempBox.style.transformOrigin = 'top left';
      tempBox.style.willChange = 'transform';
      tempBox.style.backfaceVisibility = 'hidden';
      tempBox.style.border = '2px dashed #4285F4';
      tempBox.style.background = 'rgba(66,133,244,0.06)';
      tempBox.style.boxSizing = 'border-box';
      tempBox.style.padding = '4px';
      tempBox.style.overflow = 'hidden';
      tempBox.style.pointerEvents = 'none';
      createPreviewContent();
      updateDrawPreview(1, 1, x1, y1);
      lockScreen.appendChild(tempBox);
    };

    const mouseMove = (e) => {
      if (!draw) return;
      x2 = e.offsetX;
      y2 = e.offsetY;
      scheduleDrawPreview(
        Math.abs(x2 - x1),
        Math.abs(y2 - y1),
        Math.min(x1, x2),
        Math.min(y1, y2)
      );
    };

    const mouseUp = () => {
      if (previewRaf) {
        cancelAnimationFrame(previewRaf);
        previewRaf = null;
      }
      pendingPreview = null;
      if (tempBox) lockScreen.removeChild(tempBox);
      if (isMasterSlide && masterSlideId) activeSlideId = masterSlideId;

      if (x2 === 0 || x2 === undefined) x2 = x1 + 320;
      if (y2 === 0 || y2 === undefined) y2 = y1 + 100;

      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);

      const wordArtId = `wordart-${v4()}`;
      const wordArtContent = createWordArtTextObject(presetId);
      const style = {
        width: `${(width / PAGE_WIDTH) * 100}%`,
        height: `${(height / PAGE_HEIGHT) * 100}%`,
        position: 'absolute',
        left: `${(left / PAGE_WIDTH) * 100}%`,
        top: `${(top / PAGE_HEIGHT) * 100}%`,
        zIndex: 100,
      };

      const elementData = {
        wordArtId,
        currentSlideId: activeSlideId,
        style,
        data: wordArtContent,
      };

      const dbPayload = { ...wordArtContent, styles: style };

      const isMainMasterSlide =
        isMasterSlide &&
        !Object.values(LayoutEnum).some((layout) =>
          currentSlideId.toLowerCase().includes(layout.toLowerCase())
        );

      // The id of the object that actually lands on the slide being edited - the
      // non-main master branch below renames it. Requested before any dispatch so
      // the flag is already set whenever React gets around to mounting the object,
      // and never emitted over the socket, so only the author who inserted it is
      // put into edit mode.
      const insertedId =
        isMasterSlide && !isMainMasterSlide
          ? `${currentSlideId.split('-')[2]}-${wordArtId}`
          : wordArtId;
      requestWordArtEdit(insertedId);

      if (!isMasterSlide) {
        dispatch(slideAction.addWordArtTextObject(elementData));
        const updatedSlides = getUpdatedSlides(elementData, 'wordart', existingSlides);
        slideAPI.createNewElementInDatabase(
          presentationId,
          activeSlideId,
          wordArtId,
          dbPayload,
          'wordart',
          dispatch,
          { slides: updatedSlides, mirrorSlides }
        );
      } else {
        if (isMainMasterSlide) {
          dispatch(slideAction.addWordArtTextObject(elementData));
          Object.keys(existingSlides).forEach((slideId) => {
            if (Object.values(LayoutEnum).some((v) => slideId.includes(v))) {
              dispatch(
                slideAction.addWordArtTextObject({
                  ...elementData,
                  style: { ...style, pointerEvents: 'none' },
                  currentSlideId: slideId,
                })
              );
            }
          });
          slideAPI.createMultipleNewElementInDatabase(
            presentationId,
            wordArtId,
            dbPayload,
            'wordart'
          );
        } else {
          const elementId = insertedId;
          dispatch(
            slideAction.addWordArtTextObject({ ...elementData, wordArtId: elementId })
          );
          slideAPI.createMultipleNewElementInDatabase(
            presentationId,
            elementId,
            dbPayload,
            'wordart',
            currentSlideData?.layoutType
          );
        }
      }

      emitElementAdd({
        roomId: presentationId,
        elementType: 'wordart',
        elementData,
      });

      slideContent.style.cursor = 'default';
      slideContent.removeChild(lockScreen);
    };

    lockScreen.addEventListener('mousedown', mouseDown, { once: true });
    lockScreen.addEventListener('mousemove', mouseMove, false);
    lockScreen.addEventListener('mouseup', mouseUp, { once: true });
  };

  const menu = (
    <Menu id="wordart-menu" className={classes.wordArtMenu} style={{ maxHeight: 360, overflowY: 'auto', width: 220 }}>
      {Object.entries(WORD_ART_PRESETS).map(([key, preset]) => (
        <Menu.Item key={key} onClick={() => insertWordArt(key)}>
          <div className={classes.menuItem}>
            <PresetPreview preset={preset} />
            <div className={classes.menuLabel}>{preset.label}</div>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']} onVisibleChange={onMenuOpenChange} disabled={!currentSlideId}>
      <span style={{ color: 'inherit' }} onClick={(e) => e.preventDefault()}>
        <TextFieldsIcon
          fontSize="medium"
          style={{ width: '25px', height: '22px', verticalAlign: 'top' }}
          alt="WordArt"
        />
      </span>
    </Dropdown>
  );
};

export default WordArtDropdown;
